import { Injectable, ErrorHandler, inject } from '@angular/core';
import { BehaviorSubject, ReplaySubject, of, Observable } from 'rxjs';
import { CmsApiService } from '../api/cms-api.service';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { GameAttribute } from './game-attribute';
import { CmsContentMapperService } from '../cms-content-mapper.service';
import { GameProvidersService } from './game-providers.service';
import { UpdateOnLangChange } from '../../shared/decorators/update-on-lang-change';
import { HostRequestsInterceptor } from '../../interceptors/host-requests.interceptor';
import { PlatformGraphQlService } from '../graphql/platform-graphql.service';
import { UserService } from '../user/user.service';
import { GamesLauncherService } from './games-launcher.service';
import { GameSyncError } from '../../shared/errors/game-sync';
import { ApolloClientErrorCodes } from '../graphql/errors';

interface GamesCount {
  totalCount: number;
}

interface GamesList {
  gameList: any[];
  attrList: any[];
  countTotal: number;
  countWithoutSearch?: number;
  providerList?: any[];
}

interface ResolvedImage {
  image: string;
  alternativeImage: string;
}

@Injectable({
  providedIn: 'root',
})
export class GamesService {
  private _providers = inject(GameProvidersService);
  private _api = inject(CmsApiService);
  private _cmsMapper = inject(CmsContentMapperService);
  private _hostInterceptor = inject(HostRequestsInterceptor);
  private _platformApi = inject(PlatformGraphQlService);
  private _user = inject(UserService);
  private _launcher = inject(GamesLauncherService);
  private _errorHandler = inject(ErrorHandler);

  /**
   * Hold count of loaded games
   */
  public gamesCount$: BehaviorSubject<GamesCount | null> = new BehaviorSubject<GamesCount | null>(null);

  /**
   * Use this function for loading games from cms
   *
   * @param params
   * @param cache
   */
  list(params: object = {}, cache: boolean = true): ReplaySubject<any> {
    return this._providers.list$.pipe(
      switchMap(() => (cache ? this._api.gameList(params) : this._api.gameListNoCache(params))),
      map(response => this._gameListResponseMapper(response.data)),
    ) as ReplaySubject<any>;
  }

  /**
   * Use this function for loading game from cms
   *
   * @param params
   * @param cache
   */
  itemCms(params: object = {}, cache: boolean = true): ReplaySubject<any> {
    return this._providers.list$.pipe(
      switchMap(() => (cache ? this._api.gameList(params) : this._api.gameListNoCache(params))),
      map(response => this._gameListResponseMapper(response.data)),
      map(res => res?.gameList[0]),
    ) as ReplaySubject<any>;
  }

  /**
   * The function `gameLaunchUrlSession` creates a game session using the platform API and returns the
   * result.
   * @param game - The `game` parameter is an object that contains information about the game. It likely
   * has a property called `externalId` which represents the unique identifier of the game.
   * @returns the result of the API call to create a game session. The result is obtained from the
   * response object and can be either `res?.data?.site_createGameSession?.result` or `res?.data?.result`
   * or `null`.
   */
  gameLaunchUrlSession(game) {
    return this._platformApi.createGameSession({ walletId: this._user.info.activeWallet.id, gameId: game?.externalId }).pipe(
      map((res: any) => res?.data?.site_createGameSession?.result || res?.data?.result || null),
      catchError(error => {
        this._resolveGameError(error);
        return of(null);
      }),
    );
  }

  /**
   * The function `gameDemoLaunchUrlSession` creates a demo game session using the platform API and
   * returns the result.
   * @param game - The `game` parameter is an object that contains information about the game. It likely
   * has properties such as `externalId`, which is a unique identifier for the game.
   * @returns the result of the createDemoGameSession API call. It is using the walletId from the active
   * user's wallet and the gameId provided as parameters. The result is then mapped to extract the
   * "result" property from the response data.
   */
  gameDemoLaunchUrlSession(game) {
    return this._platformApi.createDemoGameSession({ walletId: null, gameId: game?.externalId }).pipe(
      map((res: any) => res?.data?.site_createDemoGameSession?.result || res?.data?.result || null),
      catchError(error => {
        this._resolveGameError(error);
        return of(null);
      }),
    );
  }

  /**
   * The function "gameDataAndSession" retrieves game data from an item CMS, retrieves a game launch URL
   * session, and combines the game data with the launch URL session.
   * @param userAuth - is user logged in
   * @param {object} params - An object containing parameters for the itemCms function.
   * @param {boolean} [cache=true] - The `cache` parameter is a boolean value that determines whether the
   * data should be cached or not. If `cache` is set to `true`, the data will be cached, otherwise it
   * will not be cached.
   * @returns an Observable that emits an object. The object contains the game data obtained from the
   * itemCms() method, along with the launchURL obtained from the gameLaunchUrlSession() method.
   */
  gameDataAndSession(params: object = {}, cache: boolean = true) {
    let gameData = null;
    return this.itemCms(params, cache).pipe(
      tap(data => {
        gameData = data;
      }),
      switchMap(game => {
        this._launcher.checkGameAvailability(game);
        return of(game);
      }),
      switchMap(game => {
        if (!this._user.auth && !game.nodemo) {
          return this.gameDemoLaunchUrlSession(game);
        } else {
          return this.gameLaunchUrlSession(game);
        }
      }),
      map(session => {
        if (!this._user.auth && !gameData.nodemo) {
          return { ...gameData, launchURL: null, demoLaunchURL: session };
        } else {
          return { ...gameData, launchURL: session, demoLaunchURL: null };
        }
      }),
    );
  }

  /**
   * The function "gameSessions" returns an observable that combines the results of two separate
   * observables and maps them into a new object.
   * @param game - The `game` parameter is an object that represents a game. It likely contains various
   * properties such as the game's name, description, and other relevant information.
   * @param isUserAuth - flag is user logged in
   * @returns The function `gameSessions` returns an Observable that emits an object with the properties
   * `launchURL` and `demoLaunchURL`.
   */
  gameSessions(game, isUserAuth: boolean) {
    if (isUserAuth) {
      return this.gameLaunchUrlSession(game).pipe(
        map(session => {
          return { ...game, launchURL: session, demoLaunchURL: null };
        }),
      );
    } else {
      return this.gameDemoLaunchUrlSession(game).pipe(
        map(session => {
          return { ...game, launchURL: null, demoLaunchURL: session };
        }),
      );
    }
  }

  /**
   * Use this function for loading game content from cms
   *
   * @param params
   */
  @UpdateOnLangChange('/game/content/item')
  item(params: object = {}): ReplaySubject<any> {
    return this._api
      .gameContent(params)
      .pipe(map(res => this._cmsMapper.mapCmsTemplate(res.data.list ? { content: res.data.list[0].content } : null))) as ReplaySubject<any>;
  }

  /**
   * Prepare game list response for using in frontend
   *
   * @param data
   * @private
   */
  private _gameListResponseMapper(data: GamesList): GamesList {
    return {
      gameList: this.gameListMapper(data && data.gameList),
      attrList: (data && data.attrList) || [],
      providerList: (data && data.providerList) || [],
      countTotal: data.countTotal,
    };
  }

  /**
   * Prepare game list for using in frontend
   *
   * @param list
   * @private
   */
  public gameListMapper(list: Array<any>): Array<any> {
    if (!list) {
      return [];
    }

    return list
      .filter(game => game.externalData)
      .map(game => {
        game.attrTranslate = game.attrTranslate || [];
        const image: ResolvedImage = this._resolveImageSrc(game);

        return {
          ...game,
          provider: this._providers.getById(game.providerId),
          imgSrc: image.image,
          altImgSrc: image.alternativeImage,
          hot: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.HOT),
          bitcoin: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.BITCOIN),
          new: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.NEW),
          nodemo: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.NODEMO),
          flash: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.FLASH),
          dailyDrops: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.DAILY_DROPS),
          preRelease: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.PRE_RELEASE),
          jackpot: (game.attrTranslate || []).some((attribute: any) => attribute.attrId === GameAttribute.JACKPOT),
        };
      });
  }

  /**
   * Returns image src for provided game
   *
   * @param game
   * @private
   */
  private _resolveImageSrc(game: any): ResolvedImage {
    /**
     * Uses for detecting which image use (From CMS or from SS)
     */
    const extensionList = ['.jpg', '.jpeg', '.bmp', '.gif', '.png', '.svg', '.webp'];

    const cmsImage: string = environment.assets_host + game.imgSrc;

    return { image: this._hostInterceptor.overrideUrl(cmsImage), alternativeImage: cmsImage };
  }

  /**
   * Get game by identifier from SS
   * @param identifier
   */
  public getGameByExternalId(identifier: string): Observable<any> {
    return this.list({
      'external_id[]': identifier,
      'offset': 0,
      'limit': 25,
    }).pipe(
      filter(data => data.gameList[0]),
      map(data => data.gameList[0]),
    );
  }

  private _resolveGameError(error) {
    if (error?.message?.includes(ApolloClientErrorCodes.GAME_NOT_FOUND_BY_UUID)) {
      const regex = /Game with id: '([0-9a-fA-F-]+)'/;
      const match = error?.message?.match(regex);
      this._errorHandler.handleError(new GameSyncError(match[1]));
    }
  }
}
